import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { ApiError } from "types/ApiTypes";

export type ErrorType = FetchBaseQueryError | ApiError;

export const getErrorMessage = (
  error: ErrorType | undefined,
  fieldName: string,
): string | undefined => {
  if (error && "data" in error) {
    const apiError = error as ApiError;
    return apiError.data && apiError.data[fieldName]
      ? apiError.data[fieldName][0]
      : undefined;
  }
  return undefined;
};

export const hasApiError = (error: unknown): error is ApiError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "data" in error &&
    typeof (error as ApiError).data === "object"
  );
};

export const isFetchBaseQueryError = (
  error: unknown,
): error is FetchBaseQueryError => {
  return typeof error === "object" && error !== null && "status" in error;
};

/**
 * Extracts a user-friendly error message from API/DRF errors
 * Handles various error formats including field-specific validation errors
 *
 * @param error - The error object from the API call
 * @param defaultMessage - Default message if error parsing fails (default: "An error occurred")
 * @returns A formatted error message string
 *
 * @example
 * // Simple detail message
 * extractErrorMessage(error) // => "Invalid data provided"
 *
 * @example
 * // Field-specific errors
 * extractErrorMessage(error) // => "first_name: This field is required; email: Enter a valid email"
 */
export const extractErrorMessage = (
  error: unknown,
  defaultMessage = "An error occurred",
): string => {
  const requestTooLargeMessage =
    "Your upload is too large. Please upload fewer or smaller files (maximum total upload size is 1 GB).";

  // handle null/undefined errors
  if (!error) {
    return defaultMessage;
  }

  if (typeof error === "object" && error !== null) {
    const maybeApiError = error as {
      status?: unknown;
      originalStatus?: unknown;
      data?: unknown;
    };
    const isRequestTooLargeStatus =
      maybeApiError.status === 413 || maybeApiError.originalStatus === 413;
    const isLegacyRequestTooLargeParsingError =
      maybeApiError.status === "PARSING_ERROR" &&
      typeof maybeApiError.data === "string" &&
      maybeApiError.data.includes("RequestDataTooBig");

    if (isRequestTooLargeStatus || isLegacyRequestTooLargeParsingError) {
      return requestTooLargeMessage;
    }
  }

  // check if error has data property (DRF/API error)
  if (typeof error === "object" && error !== null && "data" in error) {
    const apiError = error as { data?: unknown };

    // handle array format (DRF ValidationError without field name)
    if (Array.isArray(apiError.data) && apiError.data.length > 0) {
      return String(apiError.data[0]);
    }

    if (
      apiError.data &&
      typeof apiError.data === "object" &&
      apiError.data !== null
    ) {
      const errorData = apiError.data as Record<string, unknown>;

      // handle detail message (common DRF error format)
      if ("detail" in errorData && errorData.detail) {
        return Array.isArray(errorData.detail)
          ? String(errorData.detail[0])
          : String(errorData.detail);
      }

      // handle error message (alternative error format)
      if ("error" in errorData && errorData.error) {
        return Array.isArray(errorData.error)
          ? String(errorData.error[0])
          : String(errorData.error);
      }

      // handle field-specific errors
      const fieldErrors = Object.entries(errorData)
        .filter(([key]) => key !== "detail" && key !== "error") // exclude detail and error if they exist
        .map(([fieldName, messages]) => {
          const messageArray = Array.isArray(messages) ? messages : [messages];
          return `${fieldName}: ${messageArray.join(", ")}`;
        })
        .filter((msg) => msg);

      if (fieldErrors.length > 0) {
        return fieldErrors.join("; ");
      }
    }
  }

  // handle error objects with message property
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }

  // handle string errors
  if (typeof error === "string") {
    return error;
  }

  return defaultMessage;
};
